import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Megaphone, UserPlus, Users, ShoppingCart, Truck, Package, BarChart3, Landmark,
  Clock, UserX, Inbox, FileWarning, UserCheck, Hourglass, LogIn,
  Scale, AlertTriangle, Check, X,
} from "lucide-react";
import { LoadingScreen } from "../../../components/admin/EssUi";
import EmployeeAvatar from "../../../components/admin/EmployeeAvatar";
import { get, put } from "../../../services/lewooApi";
import { api } from "../../../services/erpApi";

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = typeof d === "string" ? new Date(d.split(" ")[0]) : new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
};

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return new Date(a.split(" ")[0]).toDateString() === new Date(b.split(" ")[0]).toDateString();
};

const fmtDays = (n) => {
  const num = Number(n || 0);
  const clean = String(Math.round(num * 100) / 100);
  return `${clean} ${num === 1 ? "day" : "days"}`;
};

const fmtMoney = (v) => {
  const n = Number(v) || 0;
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 0 })} XAF`;
};

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";

const KPI_TONES = {
  neutral: "#64748b",
  green: GREEN,
  orange: "#c2570a",
  red: "#c72d33",
};

const Metric = ({ label, value, icon: Icon, tone = "neutral", trend }) => {
  const color = KPI_TONES[tone] || KPI_TONES.neutral;
  return (
    <div className="ess-kpi">
      <div className="ess-kpi-top">
        {Icon && <Icon size={15} style={{ color }} />}
        <span className="ess-kpi-label">{label}</span>
      </div>
      <div className="ess-kpi-value">{value == null ? "—" : value}</div>
      {trend && <div className="ess-kpi-trend">{trend}</div>}
    </div>
  );
};

const DetailRow = ({ label, value, tone }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "7px 0", fontSize: 13 }}>
    <span style={{ color: "#64748b" }}>{label}</span>
    <span style={{ fontWeight: 600, color: tone || "#1e293b" }}>{value}</span>
  </div>
);

const TypeBadge = ({ label }) => (
  <span className="ess-chip ess-chip-gray" title={label}>
    {label}
  </span>
);

const AdminPanelDashboardPage = () => {
  const navigate = useNavigate();
  const { permissions } = useSelector((state) => state.auth) || {};
  const [loaded, setLoaded] = useState(false);
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [acting, setActing] = useState({});
  const [actionError, setActionError] = useState("");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 8;
  const [salesDash, setSalesDash] = useState(null);
  const [purchaseDash, setPurchaseDash] = useState(null);
  const [invDash, setInvDash] = useState(null);
  const [plData, setPlData] = useState(null);
  const [arpTotals, setArpTotals] = useState({ receivables: 0, payables: 0 });

  const canViewSales = !!permissions?.canViewSales;
  const canViewPurchases = !!permissions?.canViewPurchases;
  const canViewStock = !!permissions?.canViewStock;
  const canViewReports = !!permissions?.canViewReports;
  const canViewAging = !!permissions?.canViewAging;
  const erpVisible = canViewSales || canViewPurchases || canViewStock || canViewReports || canViewAging;

  const isLeavePending = (s) => s === "PENDING" || s === "APPROVED_L1";
  const isExpensePending = (s) => s === "submitted";

  const loadData = useCallback(async () => {
    const td = new Date().toISOString().split("T")[0];

    try {
      const empRes = await get("/api/employees?limit=500");
      const allEmployees = empRes?.data || [];
      const empCount = allEmployees.length;
      const empMap = {};
      for (const emp of allEmployees) { empMap[emp.id] = emp; }

      const [leaveRes, expRes, attRes] = await Promise.all([
        get("/api/leave-requests"),
        get("/api/expenses"),
        get("/api/attendance"),
      ]);
      const allLeaves = leaveRes?.data || [];
      const allExpenses = expRes?.data || [];
      const allAttendance = attRes?.data || [];

      const leavePending = allLeaves.filter((l) => isLeavePending(l.state)).length;
      const expensePending = allExpenses.filter((e) => isExpensePending(e.state)).length;

      const todayAttendances = allAttendance.filter((a) => {
        if (!a.check_in) return false;
        return new Date(a.check_in).toISOString().split("T")[0] === td;
      });
      const checkedIn = todayAttendances.length;
      const lateArrivals = todayAttendances.filter((a) => {
        if (!a.check_in) return false;
        const d = new Date(a.check_in);
        return d.getHours() >= 9 && d.getMinutes() > 0;
      }).length;

      const todayLeaveIds = new Set(
        allLeaves.filter((l) => l.state === "APPROVED" && l.date_from <= td && l.date_to >= td)
          .map((l) => l.employee_id).filter(Boolean)
      );
      const onLeaveToday = todayLeaveIds.size;
      const absent = empCount - checkedIn - onLeaveToday;

      const contractsExpiring = allEmployees.filter((e) => {
        if (!e.contract_end_date) return false;
        const d = new Date(e.contract_end_date);
        const diff = (d - new Date()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 90;
      }).length;

      const bdays = allEmployees.filter((e) => {
        if (!e.birthday) return false;
        const bd = new Date(e.birthday);
        return bd.getMonth() === new Date().getMonth() && bd.getDate() === new Date().getDate();
      }).length;

      const active = allEmployees.filter((e) => e.active !== false).length;
      const onProbation = allEmployees.filter((e) => e.contract_type === "probation" || e.is_on_probation).length;
      const withContract = allEmployees.filter((e) => e.contract_end_date).length;

      setStats({
        empCount, checkedIn, lateArrivals, onLeaveToday, absent,
        leavePending, expensePending, contractsExpiring, bdays,
        active, onProbation, withContract,
        absentPct: empCount > 0 ? Math.round((absent / empCount) * 100) : 0,
      });

      const pendingLeaves = allLeaves.filter((l) => isLeavePending(l.state));
      const pendingExpenses = allExpenses.filter((e) => isExpensePending(e.state));

      const pItems = [];
      for (const l of pendingLeaves) {
        const emp = empMap[l.employee_id] || {};
        const name = l.employee_name || emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "—";
        pItems.push({
          id: l.id, kind: "leave", emp: name, empObj: emp, type: l.leave_type_name || l.holiday_status_id || "Leave",
          date: sameDay(l.date_from, l.date_to) ? fmtDate(l.date_from) : `${fmtDate(l.date_from)} - ${fmtDate(l.date_to)}`,
          detail: fmtDays(l.number_of_days), priority: "Medium",
        });
      }
      for (const e of pendingExpenses) {
        const emp = empMap[e.employee_id] || {};
        const name = e.employee_name || emp.name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "—";
        pItems.push({
          id: e.id, kind: "expense", emp: name, empObj: emp, type: "Expense Claim",
          date: fmtDate(e.date), detail: `${Number(e.amount || 0).toLocaleString()} XAF`, priority: "Medium",
        });
      }
      setPending(pItems);

      setLoaded(true);
    } catch {
      setLoaded(true);
    }
  }, []);

  const loadErp = useCallback(async () => {
    if (!erpVisible) return;
    try {
      const jobs = [];
      if (canViewSales) jobs.push(api.sales.dashboard().then((d) => setSalesDash(d || null)).catch(() => setSalesDash(null)));
      if (canViewPurchases) jobs.push(api.purchase.dashboard().then((d) => setPurchaseDash(d || null)).catch(() => setPurchaseDash(null)));
      if (canViewStock) jobs.push(api.inventory.dashboard().then((d) => setInvDash(d || null)).catch(() => setInvDash(null)));
      if (canViewReports) jobs.push(api.reports.profitLoss().then((d) => setPlData(d || null)).catch(() => setPlData(null)));
      if (canViewAging) {
        jobs.push(api.accounting.receivables({ limit: 1 }).then((d) => setArpTotals((t) => ({ ...t, receivables: d?.total || 0 }))).catch(() => {}));
        jobs.push(api.accounting.payables({ limit: 1 }).then((d) => setArpTotals((t) => ({ ...t, payables: d?.total || 0 }))).catch(() => {}));
      }
      await Promise.all(jobs);
    } catch {}
  }, [erpVisible, canViewSales, canViewPurchases, canViewStock, canViewReports, canViewAging]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadErp(); }, [loadErp]);
  useEffect(() => { setPage(1); }, [pending.length]);

  const handleApprove = async (item) => {
    const key = `${item.kind}-${item.id}`;
    setActing((a) => ({ ...a, [key]: "approve" }));
    setActionError("");
    try {
      if (item.kind === "leave") await put(`/api/leave-requests/${item.id}`, { state: "APPROVED" });
      else await put(`/api/expenses/${item.id}`, { state: "approved" });
      await loadData();
    } catch (e) {
      setActionError(`Approve failed: ${e?.message || "Unknown error"}`);
    }
    setActing((a) => ({ ...a, [key]: null }));
  };

  const handleReject = async (item) => {
    const key = `${item.kind}-${item.id}`;
    setActing((a) => ({ ...a, [key]: "reject" }));
    setActionError("");
    try {
      if (item.kind === "leave") await put(`/api/leave-requests/${item.id}`, { state: "REFUSED" });
      else await put(`/api/expenses/${item.id}`, { state: "refused" });
      await loadData();
    } catch (e) {
      setActionError(`Reject failed: ${e?.message || "Unknown error"}`);
    }
    setActing((a) => ({ ...a, [key]: null }));
  };

  const totalPages = Math.max(1, Math.ceil(pending.length / PAGE_SIZE));
  const paginatedPending = pending.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (!loaded) return <LoadingScreen />;

  const attentionPending = (stats?.leavePending || 0) + (stats?.expensePending || 0);
  const activePct = stats && stats.empCount > 0 ? Math.round((stats.active / stats.empCount) * 100) : 0;

  return (
    <div className="animate-fade-in" style={{ maxWidth: "1400px", margin: "0 auto" }}>
      <header className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[22px] font-semibold text-slate-900 leading-tight">ESS Admin Dashboard</h1>
          <p className="text-[13px] text-slate-500 mt-1">All employee self-service activity at a glance</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <button onClick={() => navigate("/admin-panel/employees", { state: { openAdd: true } })} className="ess-btn ess-btn-primary">
            <UserPlus size={15} />
            Add Employee
          </button>
          <button onClick={() => navigate("/admin-panel/announcements", { state: { openAdd: true } })} className="ess-btn ess-btn-secondary">
            <Megaphone size={15} />
            Send Announcement
          </button>
        </div>
      </header>

      <section className="mb-5">
        <div className="ess-kband">
          <Metric label="Late Arrivals" value={stats?.lateArrivals} icon={Clock} tone="orange" trend="after 09:00" />
          <Metric label="Absent" value={stats?.absent} icon={UserX} tone="red" trend={`${stats?.absentPct || 0}% rate`} />
          <Metric label="Pending Approvals" value={attentionPending} icon={Inbox} tone="orange" trend="Leaves + expenses" />
          <Metric label="Contracts Expiring" value={stats?.contractsExpiring} icon={FileWarning} tone="orange" trend="next 90 days" />
        </div>
      </section>

      <div style={{ display: "grid", gap: 16, alignItems: "start" }} className="mb-5 ess-rg-2-1">
        <div className="ess-panel">
          <div className="ess-panel-h">
            <div className="ess-panel-t">
              <Inbox size={16} style={{ color: "#475569" }} />
              Pending Approvals Queue
            </div>
            <span className="text-[12px] text-slate-400">{pending.length} awaiting action</span>
          </div>

          {actionError && (
            <div style={{ background: "#fff1f2", border: "1px solid #fecdd3", color: "#be123c", padding: "8px 12px", borderRadius: "6px", fontSize: "12.5px", margin: "12px 16px 0" }}>
              {actionError}
            </div>
          )}

          {pending.length === 0 ? (
            <div className="ess-panel-body">
              <div className="ess-empty">No pending approvals awaiting action.</div>
            </div>
          ) : (
            <div>
              <div style={{ overflowX: "auto" }}>
                <table className="ess-tbl" style={{ minWidth: "660px" }}>
                  <thead>
                    <tr>
                      <th className="ess-th">Employee</th>
                      <th className="ess-th">Request Type</th>
                      <th className="ess-th">Date</th>
                      <th className="ess-th">Duration</th>
                      <th className="ess-th">Status</th>
                      <th className="ess-th ess-th-r">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPending.map((p) => {
                      const rowKey = `${p.kind}-${p.id}`;
                      const busy = acting[rowKey];
                      return (
                        <tr key={rowKey} className="ess-tr">
                          <td className="ess-td" style={{ maxWidth: "200px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <EmployeeAvatar employee={p.empObj && p.empObj.id ? p.empObj : { name: p.emp }} size={26} fontSize={10} />
                              <span title={p.emp} style={{ fontSize: 13, fontWeight: 500, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "170px" }}>{p.emp}</span>
                            </div>
                          </td>
                          <td className="ess-td" style={{ maxWidth: "190px" }}><TypeBadge label={p.type} /></td>
                          <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{p.date}</td>
                          <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap" }}>{p.detail}</td>
                          <td className="ess-td"><span className="ess-chip ess-chip-orange">Pending</span></td>
                          <td className="ess-td" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                            <div style={{ display: "inline-flex", gap: "6px" }}>
                              <button
                                disabled={!!busy}
                                onClick={() => handleApprove(p)}
                                className="ess-btn ess-btn-primary"
                                style={{ padding: "5px 10px", fontSize: "12px" }}
                              >{busy === "approve" ? "…" : <><Check size={13} /> Approve</>}</button>
                              <button
                                disabled={!!busy}
                                onClick={() => handleReject(p)}
                                title="Reject"
                                className="ess-btn ess-btn-danger"
                                style={{ padding: "5px 10px", fontSize: "12px" }}
                              >{busy === "reject" ? "…" : <><X size={13} /> Reject</>}</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "6px", padding: "10px 16px", borderTop: `1px solid ${BORDER}` }}>
                  <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="ess-btn ess-btn-secondary" style={{ padding: "5px 10px", fontSize: "12px" }}>‹ Prev</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} onClick={() => setPage(p)} style={{ padding: "5px 10px", minWidth: "28px", borderRadius: "6px", border: "none", cursor: "pointer", fontSize: "12px", fontWeight: p === page ? 600 : 400, background: p === page ? GREEN : "transparent", color: p === page ? "#fff" : "#64748b" }}>{p}</button>
                  ))}
                  <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="ess-btn ess-btn-secondary" style={{ padding: "5px 10px", fontSize: "12px" }}>Next ›</button>
                  <span style={{ fontSize: "11px", color: "#94a3b8", marginLeft: "8px" }}>{pending.length} total</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="ess-panel">
            <div className="ess-panel-h">
              <div className="ess-panel-t">
                <Users size={16} style={{ color: "#475569" }} />
                Workforce
              </div>
            </div>
            <div className="ess-panel-body">
              <div className="flex items-baseline gap-2">
                <span style={{ fontSize: 28, fontWeight: 600, color: "#0f172a", lineHeight: 1 }}>{stats?.empCount ?? "—"}</span>
                <span style={{ fontSize: 13, color: "#64748b" }}>Total employees</span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: "#eef1ee", overflow: "hidden", marginTop: 12 }}>
                <div style={{ height: "100%", width: `${activePct}%`, background: GREEN, borderRadius: 3 }} />
              </div>
              <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 6 }}>{stats?.active ?? "—"} of {stats?.empCount ?? "—"} active · {stats?.withContract ?? "—"} with contract</p>
              <div style={{ marginTop: 10 }}>
                <DetailRow label="Active" value={stats?.active ?? "—"} tone={GREEN} />
                <DetailRow label="On probation" value={stats?.onProbation ?? "—"} />
                <DetailRow label="On leave today" value={stats?.onLeaveToday ?? "—"} />
                <DetailRow label="Birthdays today" value={stats?.bdays ?? "—"} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mb-5">
        <div className="ess-kband">
          <Metric label="Total Employees" value={stats?.empCount} icon={Users} tone="green" trend="Headcount" />
          <Metric label="Active" value={stats?.active} icon={UserCheck} tone="green" trend="Current staff" />
          <Metric label="On Probation" value={stats?.onProbation} icon={Hourglass} trend="Trial period" />
          <Metric label="Checked In Today" value={stats?.checkedIn} icon={LogIn} tone="green" trend={`of ${stats?.empCount ?? 0} headcount`} />
        </div>
      </section>

      {erpVisible && (
        <section className="mb-2">
          <div className="mb-3">
            <h3 className="flex items-center gap-2 text-[15px] font-semibold text-slate-900">
              <BarChart3 size={16} style={{ color: GREEN }} />
              ERP &amp; Finance Overview
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Sales, purchases, inventory and accounting at a glance</p>
          </div>

          <div className="ess-kband" style={{ marginBottom: 16 }}>
            {canViewSales && (
              <Metric label="Sales Revenue" value={fmtMoney(salesDash?.totals?.revenue)} icon={ShoppingCart} tone="green" trend={`${salesDash?.totals?.count || 0} confirmed orders`} />
            )}
            {canViewSales && (
              <Metric label="Sales by Status" value={String((salesDash?.by_state || []).reduce((a, r) => a + (Number(r.n) || 0), 0))} icon={BarChart3} trend={(salesDash?.by_state || []).slice(0, 3).map((r) => `${r.state}:${r.n}`).join(" · ")} />
            )}
            {canViewPurchases && (
              <Metric label="Purchase Spend" value={fmtMoney(purchaseDash?.totals?.spend)} icon={Truck} trend={`${purchaseDash?.totals?.count || 0} purchase orders`} />
            )}
            {canViewStock && (
              <Metric label="Stock Value" value={fmtMoney(invDash?.stock_value)} icon={Package} trend={`${invDash?.total_products || 0} products`} />
            )}
          </div>

          {(canViewReports || canViewAging) && (
            <div style={{ display: "grid", gap: 16 }} className="mb-4 ess-rg-2-1">
              {canViewReports && (
                <div className="ess-panel">
                  <div className="ess-panel-h">
                    <div className="ess-panel-t">
                      <Landmark size={16} style={{ color: "#475569" }} />
                      Profit &amp; Loss
                    </div>
                    <span className="text-[12px] text-slate-400">Posted account moves</span>
                  </div>
                  <div className="ess-panel-body">
                    {!plData ? (
                      <div className="ess-empty">No financial data available.</div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-md overflow-hidden" style={{ background: "#eef1ee", border: "1px solid #eef1ee" }}>
                        {[
                          { label: "Revenue", value: fmtMoney(plData.revenue_total), color: "#0f172a" },
                          { label: "COGS", value: fmtMoney(plData.cogs_total), color: "#475569" },
                          { label: "Operating", value: fmtMoney(plData.opex_total), color: "#475569" },
                          { label: "Net Profit", value: fmtMoney(plData.net_profit), color: plData.net_profit >= 0 ? GREEN : "#c72d33" },
                        ].map((k) => (
                          <div key={k.label} style={{ background: "#fff", padding: "12px 14px" }}>
                            <p style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "#64748b" }}>{k.label}</p>
                            <p style={{ fontSize: 16, fontWeight: 600, color: k.color, marginTop: 4 }}>{k.value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              {canViewAging && (
                <div className="ess-panel">
                  <div className="ess-panel-h">
                    <div className="ess-panel-t">
                      <Scale size={16} style={{ color: "#475569" }} />
                      Open Balances
                    </div>
                  </div>
                  <div className="ess-panel-body">
                    <DetailRow label="Receivables" value={fmtMoney(arpTotals.receivables)} />
                    <DetailRow label="Payables" value={fmtMoney(arpTotals.payables)} />
                  </div>
                </div>
              )}
            </div>
          )}

          {canViewStock && (
            <div style={{ display: "grid", gap: 16 }} className="mb-4 ess-rg-1-2">
              <div className="ess-panel">
                <div className="ess-panel-h">
                  <div className="ess-panel-t">
                    <AlertTriangle size={16} style={{ color: "#c2570a" }} />
                    Low Stock Alert
                  </div>
                </div>
                <div className="ess-panel-body">
                  {!invDash || !invDash.low_stock?.length ? (
                    <div className="ess-empty">No low stock items.</div>
                  ) : (
                    invDash.low_stock.slice(0, 6).map((p, idx) => (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 0", borderBottom: idx < Math.min(6, invDash.low_stock.length) - 1 ? `1px solid ${BORDER}` : "none", fontSize: 13 }}>
                        <span style={{ color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                        <span style={{ fontWeight: 600, color: "#be123c", whiteSpace: "nowrap" }}>{Number(p.available || 0).toLocaleString()} left</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="ess-panel">
                <div className="ess-panel-h">
                  <div className="ess-panel-t">
                    <Package size={16} style={{ color: "#475569" }} />
                    Recent Stock Moves
                  </div>
                  <span className="text-[12px] text-slate-400">Latest movements</span>
                </div>
                {!invDash || !invDash.recent_moves?.length ? (
                  <div className="ess-panel-body">
                    <div className="ess-empty">No stock movements recorded.</div>
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table className="ess-tbl" style={{ minWidth: "520px" }}>
                      <thead>
                        <tr>
                          <th className="ess-th">Product</th>
                          <th className="ess-th">Quantity</th>
                          <th className="ess-th">Status</th>
                          <th className="ess-th">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invDash.recent_moves.slice(0, 6).map((m, i) => (
                          <tr key={m.id || i} className="ess-tr">
                            <td className="ess-td" style={{ color: "#1e293b", maxWidth: "240px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.product_name || "—"}</td>
                            <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap" }}>{Number(m.quantity || 0).toLocaleString()}</td>
                            <td className="ess-td">
                              <span className={m.state === "done" ? "ess-chip ess-chip-green" : "ess-chip ess-chip-gray"}>{m.state || "—"}</span>
                            </td>
                            <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmtDate(m.date_done || m.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {(canViewSales || canViewPurchases) && (
            <div style={{ display: "grid", gap: 16 }} className="mb-2 ess-rg-2">
              {canViewSales && (
                <div className="ess-panel">
                  <div className="ess-panel-h">
                    <div className="ess-panel-t">
                      <Users size={16} style={{ color: "#475569" }} />
                      Top Customers
                    </div>
                  </div>
                  <div className="ess-panel-body">
                    {!salesDash || !salesDash.top_customers?.length ? (
                      <div className="ess-empty">No customers available.</div>
                    ) : (
                      salesDash.top_customers.map((c, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 0", borderBottom: idx < salesDash.top_customers.length - 1 ? `1px solid ${BORDER}` : "none", fontSize: 13 }}>
                          <span style={{ color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.name}</span>
                          <span style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{fmtMoney(c.revenue)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {canViewPurchases && (
                <div className="ess-panel">
                  <div className="ess-panel-h">
                    <div className="ess-panel-t">
                      <Truck size={16} style={{ color: "#475569" }} />
                      Top Vendors
                    </div>
                  </div>
                  <div className="ess-panel-body">
                    {!purchaseDash || !purchaseDash.top_vendors?.length ? (
                      <div className="ess-empty">No vendors available.</div>
                    ) : (
                      purchaseDash.top_vendors.map((v, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 0", borderBottom: idx < purchaseDash.top_vendors.length - 1 ? `1px solid ${BORDER}` : "none", fontSize: 13 }}>
                          <span style={{ color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</span>
                          <span style={{ fontWeight: 600, color: "#1e293b", whiteSpace: "nowrap" }}>{fmtMoney(v.spend)}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminPanelDashboardPage;